import ChatVoiceHint from "@mapmycustomers/shared/enum/chat/ChatVoiceHint";

import { VoiceTunables } from "./consts";
import { initialHintState, reduceHint } from "./hints";

const FFT_SIZE = 2048;
// Deliberately not requestAnimationFrame: that is a rendering callback, so the silence detector
// would sample at whatever rate the UI happens to render at and inherit every hitch in it. At
// 20ms each read still overlaps the analyser's ~43ms window, so no audio goes unobserved.
const SAMPLE_INTERVAL_MS = 20;

export interface LevelAnalyser {
  // Last computed RMS, polled rather than pushed: sixty dispatches a second through Redux would
  // make every connected component in the app re-check its props sixty times a second.
  getLevel(): number;
  stop(): void;
}

const createLevelAnalyser = ({
  audioContext,
  onFrame,
  onHintChange,
  stream,
  tunables,
}: {
  audioContext: AudioContext;
  onFrame: (rms: number, dtMs: number) => void;
  onHintChange: (hint: ChatVoiceHint) => void;
  stream: MediaStream;
  tunables: VoiceTunables;
}): LevelAnalyser => {
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = FFT_SIZE;
  audioContext.createMediaStreamSource(stream).connect(analyser);

  const buffer = new Float32Array(analyser.fftSize);
  let level = 0;
  let hintState = initialHintState;
  let previous = performance.now();
  let timer = 0;
  let running = true;

  const tick = () => {
    if (!running) {
      return;
    }
    const now = performance.now();
    const dtMs = now - previous;
    previous = now;

    analyser.getFloatTimeDomainData(buffer);
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      sum += buffer[i] * buffer[i];
    }
    level = Math.sqrt(sum / buffer.length);

    onFrame(level, dtMs);

    const next = reduceHint(hintState, level, dtMs, tunables);
    // Edge-triggered: fires on a transition, never once per frame.
    if (next.hint !== hintState.hint) {
      onHintChange(next.hint);
    }
    hintState = next;
  };

  timer = window.setInterval(tick, SAMPLE_INTERVAL_MS);

  return {
    getLevel: () => level,
    stop: () => {
      running = false;
      window.clearInterval(timer);
    },
  };
};

export default createLevelAnalyser;
